import api from "../../../api/axios";

const MAX_PAGES = 100;

/** Immutable list filters that must survive pagination traversal. */
export const IMMUTABLE_WORK_FILTER_KEYS = [
  "due_after",
  "due_before",
  "scheduled_after",
  "scheduled_before",
  "status",
  "responsible_user",
  "client",
  "property",
  "is_overdue",
  "ordering",
];

function getLocationContext() {
  if (typeof window !== "undefined") {
    return {
      origin: window.location.origin,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
    };
  }
  return {
    origin: "http://[REDACTED]", // pragma: allowlist secret
    hostname: "[REDACTED]", // pragma: allowlist secret
    protocol: "http:",
  };
}

function paramsFromSearchParams(searchParams) {
  const params = {};
  for (const [key, value] of searchParams.entries()) {
    if (params[key] == null) {
      params[key] = value;
    } else if (Array.isArray(params[key])) {
      params[key].push(value);
    } else {
      params[key] = [params[key], value];
    }
  }
  return params;
}

function normalizeParamValue(value) {
  if (value == null) return null;
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).sort().join("\0");
  }
  return String(value);
}

export function immutableFiltersPreserved(expectedParams, nextParams) {
  const expected = expectedParams || {};
  const actual = nextParams || {};
  for (const key of IMMUTABLE_WORK_FILTER_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(expected, key)) continue;
    const left = normalizeParamValue(expected[key]);
    const right = normalizeParamValue(actual[key]);
    if (left !== right) return false;
  }
  return true;
}

function normalizePageKey(urlString, baseOrigin) {
  const base = new URL(baseOrigin);
  const u = new URL(urlString, base);
  const entries = [...u.searchParams.entries()].sort((a, b) => {
    if (a[0] !== b[0]) return a[0].localeCompare(b[0]);
    return a[1].localeCompare(b[1]);
  });
  const search = new URLSearchParams();
  for (const [key, value] of entries) {
    search.append(key, value);
  }
  const qs = search.toString();
  return `${u.pathname}${qs ? `?${qs}` : ""}`;
}

const APPROVED_DEV_PORTS = new Set(["3000", "8000"]);

function defaultPortForProtocol(protocol) {
  return protocol === "https:" ? "443" : "80";
}

function normalizedPort(url) {
  return url.port || defaultPortForProtocol(url.protocol);
}

/**
 * Validate DRF next URL for an exact company work list path.
 * expectedPathname example: `/api/companies/12/tasks/`
 */
export function validateCompanyWorkNextLink(
  rawNext,
  locationContext,
  visited,
  expectedPathname,
  { expectedFilters = null } = {},
) {
  if (!rawNext || !expectedPathname) return null;
  try {
    const base = new URL(locationContext.origin);
    const u = new URL(rawNext, base);

    if (u.protocol !== "http:" && u.protocol !== "https:") {
      return null;
    }
    if (u.username || u.password) {
      return null;
    }
    if (u.protocol !== locationContext.protocol) {
      return null;
    }

    const sameOrigin = u.origin === locationContext.origin;
    const sameHostname = u.hostname === locationContext.hostname;
    if (!sameOrigin && !sameHostname) {
      return null;
    }

    if (sameHostname) {
      const appPort = normalizedPort(new URL(locationContext.origin));
      const nextPort = normalizedPort(u);
      const allowedPorts = new Set([appPort, ...APPROVED_DEV_PORTS]);
      if (!allowedPorts.has(nextPort)) {
        return null;
      }
    }

    if (u.pathname !== expectedPathname) {
      return null;
    }

    const params = paramsFromSearchParams(u.searchParams);
    if (
      expectedFilters &&
      !immutableFiltersPreserved(expectedFilters, params)
    ) {
      return null;
    }

    const pageKey = normalizePageKey(u.toString(), locationContext.origin);
    if (visited.has(pageKey)) {
      return null;
    }

    return {
      pageKey,
      params,
    };
  } catch {
    return null;
  }
}

export function companyWorkListPath(companyId, kind) {
  const id = String(companyId);
  if (kind === "viewing") {
    return `/api/companies/${id}/viewings/`;
  }
  return `/api/companies/${id}/tasks/`;
}

export function companyWorkApiRelative(companyId, kind) {
  const id = String(companyId);
  if (kind === "viewing") {
    return `/companies/${id}/viewings/`;
  }
  return `/companies/${id}/tasks/`;
}

function firstPageKey(apiRelative, params, locationContext) {
  const search = new URLSearchParams();
  const entries = Object.entries(params || {}).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  for (const [key, value] of entries) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  const relative = `${apiRelative.startsWith("/") ? apiRelative : `/${apiRelative}`}${qs ? `?${qs}` : ""}`;
  // Mirror DRF absolute path under /api for visited-key consistency.
  const pathname = relative.startsWith("/api/")
    ? relative.split("?")[0]
    : `/api${relative.split("?")[0]}`;
  const withQs = qs ? `${pathname}?${qs}` : pathname;
  return normalizePageKey(withQs, locationContext.origin);
}

/**
 * Fetch all pages for a company work list within a calendar window.
 * Failure / limit / unsafe next is never presented as an empty complete set.
 */
export async function fetchAllCompanyWorkPages(
  companyId,
  kind,
  params,
  { signal } = {},
) {
  const locationContext = getLocationContext();
  const expectedPathname = companyWorkListPath(companyId, kind);
  const apiRelative = companyWorkApiRelative(companyId, kind);
  const expectedFilters = { ...(params || {}) };

  const allRows = [];
  const seenIds = new Set();
  const visited = new Set();
  visited.add(firstPageKey(apiRelative, params, locationContext));

  let pageCount = 0;
  let nextParams = null;
  let isFirst = true;

  while (true) {
    if (signal && signal.aborted) {
      return { ok: false, aborted: true };
    }

    let payload;
    try {
      if (isFirst) {
        const res = await api.get(apiRelative, { params, signal });
        payload = res.data;
        isFirst = false;
      } else {
        const res = await api.get(apiRelative, {
          params: nextParams,
          signal,
        });
        payload = res.data;
      }
    } catch (err) {
      if (signal && signal.aborted) return { ok: false, aborted: true };
      const message =
        (err && err.response && err.response.data && err.response.data.detail) ||
        (err && err.message) ||
        "Помилка завантаження";
      return { ok: false, error: message };
    }

    pageCount += 1;
    if (pageCount > MAX_PAGES) {
      return {
        ok: false,
        error: "Перевищено ліміт сторінок",
        limitExceeded: true,
      };
    }

    if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
      return {
        ok: false,
        error: "Некоректна відповідь списку",
        malformed: true,
      };
    }
    if (!Array.isArray(payload.results)) {
      return {
        ok: false,
        error: "Некоректна відповідь списку",
        malformed: true,
      };
    }

    for (const row of payload.results) {
      if (row && row.id != null && !seenIds.has(row.id)) {
        seenIds.add(row.id);
        allRows.push(row);
      }
    }

    const rawNext = payload.next || null;
    if (!rawNext) break;

    if (pageCount >= MAX_PAGES) {
      return {
        ok: false,
        error: "Перевищено ліміт сторінок",
        limitExceeded: true,
      };
    }

    const validated = validateCompanyWorkNextLink(
      rawNext,
      locationContext,
      visited,
      expectedPathname,
      { expectedFilters },
    );
    if (!validated) {
      return {
        ok: false,
        error: "Небезпечне або повторне посилання на наступну сторінку",
        unsafeNext: true,
      };
    }

    visited.add(validated.pageKey);
    nextParams = validated.params;
  }

  return { ok: true, rows: allRows, pageCount };
}

export { MAX_PAGES, normalizePageKey, getLocationContext };
